import { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { GA4_MEASUREMENT_ID, GOOGLE_SEARCH_CONSOLE_TOKEN, LOGO_IMAGE, OG_IMAGE, SITE_URL, BUSINESS_NAME, buildLocalBusinessSchema, buildWebSiteSchema, seoByLang } from '../data/seo';

/** Marks every node this component owns so re-renders replace, never duplicate. */
const OWNED = 'data-seo-managed';
function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute(OWNED, '');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
function upsertLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    el.setAttribute(OWNED, '');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
function upsertJsonLd(id: string, data: unknown) {
  let el = document.head.querySelector<HTMLScriptElement>(`script#${id}`);
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    el.setAttribute(OWNED, '');
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/** Loads gtag.js once and configures the GA4 property. */
function useGoogleAnalytics() {
  useEffect(() => {
    if (!GA4_MEASUREMENT_ID) return;
    if (document.getElementById('ga4-src')) return;
    const script = document.createElement('script');
    script.id = 'ga4-src';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    document.head.appendChild(script);
    const w = window as unknown as {
      dataLayer: unknown[];
      gtag: (...args: unknown[]) => void;
    };
    w.dataLayer = w.dataLayer || [];
    w.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer.push(arguments);
    };
    w.gtag('js', new Date());
    w.gtag('config', GA4_MEASUREMENT_ID, {
      page_title: document.title,
      page_location: window.location.href,
      send_page_view: true
    });
  }, []);
}

/**
 * Injects title, SEO meta, Open Graph / Twitter cards, canonical + hreflang
 * and schema.org JSON-LD into <head>, kept in sync with the active language.
 * Renders nothing.
 */
export function Seo() {
  const {
    lang
  } = useLanguage();
  useGoogleAnalytics();
  useEffect(() => {
    const meta = seoByLang[lang];
    const alt = seoByLang[lang === 'es' ? 'en' : 'es'];
    document.title = meta.title;
    document.documentElement.lang = lang;

    // Core SEO
    upsertMeta('name', 'description', meta.description);
    upsertMeta('name', 'keywords', meta.keywords);
    upsertMeta('name', 'author', BUSINESS_NAME);
    upsertMeta('name', 'robots', 'index, follow, max-image-preview:large');
    upsertMeta('name', 'googlebot', 'index, follow');
    upsertMeta('name', 'geo.region', 'US-NY');
    upsertMeta('name', 'geo.placename', 'Staten Island');
    upsertMeta('name', 'geo.position', '40.6284;-74.1483');
    upsertMeta('name', 'ICBM', '40.6284, -74.1483');
    upsertMeta('name', 'theme-color', '#0B0B0C');
    if (GOOGLE_SEARCH_CONSOLE_TOKEN) {
      upsertMeta('name', 'google-site-verification', GOOGLE_SEARCH_CONSOLE_TOKEN);
    }

    // Open Graph
    upsertMeta('property', 'og:type', 'business.business');
    upsertMeta('property', 'og:site_name', BUSINESS_NAME);
    upsertMeta('property', 'og:title', meta.title);
    upsertMeta('property', 'og:description', meta.description);
    upsertMeta('property', 'og:url', SITE_URL);
    upsertMeta('property', 'og:image', OG_IMAGE);
    upsertMeta('property', 'og:image:alt', BUSINESS_NAME);
    upsertMeta('property', 'og:image:width', '1200');
    upsertMeta('property', 'og:image:height', '630');
    upsertMeta('property', 'og:locale', meta.locale);
    upsertMeta('property', 'og:locale:alternate', alt.locale);
    upsertMeta('property', 'business:contact_data:street_address', '2031 Forest Avenue');
    upsertMeta('property', 'business:contact_data:locality', 'Staten Island');
    upsertMeta('property', 'business:contact_data:region', 'NY');
    upsertMeta('property', 'business:contact_data:postal_code', '10303');
    upsertMeta('property', 'business:contact_data:country_name', 'USA');
    upsertMeta('property', 'business:contact_data:phone_number', '+1-718-273-3434');

    // Twitter / X
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', meta.title);
    upsertMeta('name', 'twitter:description', meta.description);
    upsertMeta('name', 'twitter:image', OG_IMAGE);
    upsertMeta('name', 'twitter:image:alt', BUSINESS_NAME);

    // Canonical, alternates, icons
    upsertLink('canonical', SITE_URL);
    upsertLink('alternate', SITE_URL, 'es');
    upsertLink('alternate', SITE_URL, 'en');
    upsertLink('alternate', SITE_URL, 'x-default');
    upsertLink('apple-touch-icon', LOGO_IMAGE);

    // Structured data
    upsertJsonLd('ld-local-business', buildLocalBusinessSchema(lang));
    upsertJsonLd('ld-website', buildWebSiteSchema(lang));
  }, [lang]);
  return null;
}